import { useReportSearchMapRowPrice } from 'components/Search/SearchResultsMap/SearchMapRowPrices'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import type { ComponentProps } from 'react'
import { useAppSelector } from 'hooks/reduxHooks'
import PriceStackPriceWithLoyalty from 'LuxKit/PricePoints/PriceStackPriceWithLoyalty'
import PriceStackPriceWithLoyaltyLeadWithRegularPrice from 'LuxKit/PricePoints/PriceStackPriceWithLoyaltyLeadWithRegularPrice'
import PriceRowLuxPlusPriceBox from 'LuxKit/PricePoints/LuxPlus/PriceRowLuxPlusPriceBox'
import PriceRowLuxPlusPrice from 'LuxKit/PricePoints/LuxPlus/PriceRowLuxPlusPrice'
import PriceStackLuxPlusCompactPrice from 'LuxKit/PricePoints/LuxPlus/PriceStackLuxPlusCompactPrice'
import PriceStackPriceWithLoyaltyLeadWithLuxPlusPrice from 'LuxKit/PricePoints/PriceStackPriceWithLoyaltyLeadWithLuxPlusPrice'
import { isGoogleHotelAdsBot } from 'selectors/accountSelectors'
import { isGhaPriceDisplay } from 'selectors/routerSelectors'
import useLuxPlusDisplayedMode from 'luxPlus/hooks/useLuxPlusDisplayedMode'
import getLuxPlusPrimaryCashPrice, { shouldShowLuxPlusNonMemberPrice } from 'luxPlus/lib/getLuxPlusPrimaryCashPrice'
import { getCanViewLuxPlusBenefits, getIsFreePreviewLuxPlusMember } from 'luxPlus/selectors/featureToggle'

type InheritedPriceRowPriceProps = Pick<ComponentProps<typeof PriceRowPrice>, 'signDisplay' | 'size'>
type InheritedPriceStackPriceWithLoyaltyProps = Pick<ComponentProps<typeof PriceStackPriceWithLoyalty>, 'horizontalAlign' | 'desktopHorizontalAlign'>

interface CommonProps extends InheritedPriceRowPriceProps, InheritedPriceStackPriceWithLoyaltyProps {
  testid?: string
  /**
   * @default vertical
   */
  orientation?: ComponentProps<typeof PriceStackPriceWithLoyaltyLeadWithLuxPlusPrice>['orientation']
  offerConfig?: App.OfferLuxPlusConfig
  isTour?: boolean
  mode?: App.LuxPlusPriceStackMode
  /** Opt-in price-block experiment layout shared by both variants. Omit for control. */
  presentation?: 'price-block-experiment'
}

interface PropsWithSeparatePriceFields extends CommonProps {
  price: number
  saleUnit: string
  memberPrice?: number
}

interface PropsWithPricingField extends CommonProps {
  pricing: App.Pricing
  memberPricing?: App.Pricing
}

type Props = PropsWithSeparatePriceFields | PropsWithPricingField

const PRICE_BLOCK_EXPERIMENT_MODES: ReadonlyArray<App.LuxPlusPriceStackMode> = [
  'regular-only', 'member-prominent', 'regular-prominent', 'member-only',
]

function LuxPlusPriceStack(props: Props) {
  const {
    size,
    testid,
    signDisplay,
    horizontalAlign = 'start',
    desktopHorizontalAlign,
    orientation = 'vertical',
    offerConfig,
    isTour = false,
    mode,
    presentation,
  } = props

  let memberPrice: number = 0
  let price: number
  let saleUnit: string

  if ('pricing' in props) {
    price = props.pricing.price
    saleUnit = props.pricing.saleUnit
    memberPrice = props.memberPricing?.price || 0
  } else {
    price = props.price
    saleUnit = props.saleUnit
    memberPrice = props.memberPrice || 0
  }

  const isGoogleHotelAds = useAppSelector(isGoogleHotelAdsBot)
  const isGhaPricing = useAppSelector(isGhaPriceDisplay)
  const isFreePreviewLuxPlusMember = useAppSelector(getIsFreePreviewLuxPlusMember)
  const canViewMemberBenefits = useAppSelector(getCanViewLuxPlusBenefits)
  const canFreePreviewBuyMemberOnly = isFreePreviewLuxPlusMember && offerConfig?.restrictPurchaseToMembers === false

  const displayedMode = useLuxPlusDisplayedMode({ price, memberPrice, offerConfig, isTour, mode })
  useReportSearchMapRowPrice(getLuxPlusPrimaryCashPrice(price, memberPrice, displayedMode, isGoogleHotelAds, isGhaPricing))
  const showNonMemberPrice = shouldShowLuxPlusNonMemberPrice(displayedMode, canFreePreviewBuyMemberOnly)
  const isPriceBlockExperimentPresentation = presentation === 'price-block-experiment' && !isGhaPricing && !isGoogleHotelAds &&
    PRICE_BLOCK_EXPERIMENT_MODES.includes(displayedMode)
  const priceBlockExperimentComparison = isPriceBlockExperimentPresentation ? (canViewMemberBenefits ? 'inline' : 'caption') : undefined

  if (isPriceBlockExperimentPresentation) {
    // Both price-block experiment variants put the priced unit in the duration caption.
    // Keep this presentation-only: the supplied pricing objects are never changed.
    saleUnit = 'total'
  }

  switch (displayedMode) {
    case 'member-only':
      if (showNonMemberPrice || (isPriceBlockExperimentPresentation && canViewMemberBenefits)) {
        return <PriceStackPriceWithLoyaltyLeadWithLuxPlusPrice
          data-testid={testid}
          memberPrice={memberPrice || price}
          nonMemberPrice={price}
          saleUnit={saleUnit}
          size={size}
          signDisplay={signDisplay}
          horizontalAlign={horizontalAlign}
          orientation={orientation}
          desktopHorizontalAlign={desktopHorizontalAlign}
          comparisonPresentation={priceBlockExperimentComparison}
        />
      }
      return <PriceRowLuxPlusPrice
        data-testid={testid}
        memberPrice={memberPrice || price}
        saleUnit={saleUnit}
        size={size}
        signDisplay={signDisplay}
        logoPlacement={isPriceBlockExperimentPresentation && horizontalAlign === 'end' ? 'leading' : 'trailing'}
        hideLogoPrefix={isPriceBlockExperimentPresentation}
      />
    case 'regular-only':
      return <PriceRowPrice
        data-testid={testid}
        price={price}
        saleUnit={saleUnit}
        size={size}
        signDisplay={signDisplay}
      />
    case 'member-prominent': {
      return <PriceStackPriceWithLoyaltyLeadWithLuxPlusPrice
        data-testid={testid}
        memberPrice={memberPrice}
        saleUnit={saleUnit}
        nonMemberPrice={price}
        size={size}
        signDisplay={signDisplay}
        horizontalAlign={horizontalAlign}
        orientation={orientation}
        desktopHorizontalAlign={desktopHorizontalAlign}
        comparisonPresentation={priceBlockExperimentComparison}
      />
    }
    case 'member-compact':
      return <PriceStackLuxPlusCompactPrice
        data-testid={testid}
        memberPrice={memberPrice}
        saleUnit={saleUnit}
        nonMemberPrice={price}
        size={size}
        horizontalAlign={horizontalAlign}
      />
    case 'regular-prominent':
      if (isGhaPricing) {
        return <PriceStackPriceWithLoyaltyLeadWithRegularPrice
          data-testid={testid}
          memberPrice={memberPrice}
          saleUnit={saleUnit}
          nonMemberPrice={price}
          size={size}
          signDisplay={signDisplay}
          horizontalAlign={horizontalAlign}
          desktopHorizontalAlign={desktopHorizontalAlign}
        />
      }
      if (isGoogleHotelAds) {
        return <PriceStackPriceWithLoyalty
          data-testid={testid}
          memberPrice={memberPrice}
          saleUnit={saleUnit}
          nonMemberPrice={price}
          size={size}
          signDisplay={signDisplay}
          horizontalAlign={horizontalAlign}
          desktopHorizontalAlign={desktopHorizontalAlign}
        />
      }
      return <PriceStackPriceWithLoyaltyLeadWithLuxPlusPrice
        data-testid={testid}
        memberPrice={memberPrice}
        saleUnit={saleUnit}
        nonMemberPrice={price}
        size={size}
        signDisplay={signDisplay}
        horizontalAlign={horizontalAlign}
        orientation={orientation}
        desktopHorizontalAlign={desktopHorizontalAlign}
        comparisonPresentation={priceBlockExperimentComparison}
      />

    case 'member-checkout-total':
      return <PriceRowLuxPlusPriceBox
        data-testid={testid}
        memberPrice={memberPrice || price}
        saleUnit={saleUnit}
        size={size}
        horizontalAlign={horizontalAlign}
      />
    case 'regular-with-luxPlus':
      return <PriceRowLuxPlusPrice
        size="L"
        memberPrice={memberPrice}
        logoPlacement="trailing"
        saleUnit=""
      />
  }
}

export default LuxPlusPriceStack
